/**
 * Consult Door B — the PUBLIC booking data-layer SEAM.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  One clean interface (`BookingApi`) the Door B page talks to, with two
 *  interchangeable implementations behind it:
 *
 *    · `liveBookingApi`  — calls the four App-Check-gated Cloud Functions
 *        (`getPublicAvailability`, `createPublicBooking`,
 *         `reschedulePublicBooking`, `cancelPublicBooking`) via the Firebase
 *        callable client. These are being built in a parallel session and are
 *        NOT deployed yet — so every call today fails and the page shows the
 *        HONEST loading/error state (never a fake "booked").
 *
 *    · `mockBookingApi` — deterministic sample data mirroring the QA-passed
 *        mockup (3 days of slots, one busy, one that "just got taken"), so
 *        Vera/Iris can walk all seven states with no backend. This is a design
 *        QA harness — the same discipline as the Door A `consult-dev.html`
 *        harness and the `/preview-*` routes — NOT a production code path.
 *
 *  THE ONE-LINE SWAP: when the CFs deploy, nothing in the page changes —
 *  `resolveBookingApi()` already returns `liveBookingApi` for the real route.
 *  App Check tokens are attached automatically once `ensureAppCheck()` has run
 *  (the site key is owner-provisioned later — see appCheck.ts).
 *
 *  The types below mirror the frozen contracts in
 *  `Deliverables/2026-07-08-consult-doorB-datamap-actions-states.md` §3.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { getAppCheckTokenBounded } from './appCheck';

/* ════════════════════ Contract types (mirror datamap §3) ════════════════════ */

/** The client-supplied booking form fields (datamap Delta A — `discussionTopic`). */
export interface PublicClient {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  projectType: string;
  /** R6 — "What would you like to discuss?" (was `brief`). */
  discussionTopic: string;
}

/** The public, non-leaking fields of the resolved appointment type. */
export interface PublicAppointmentType {
  name: string;
  slug: string;
  durationMin: number;
  description: string;
  host: string;
  price: { mode: 'free' | 'paid'; amount: number | null; currency: string | null };
}

/** One open slot — ISO instants; the client renders them in their chosen tz. */
export interface PublicSlot {
  startISO: string;
  endISO: string;
}

/** `getPublicAvailability` input (datamap §3.1). */
export interface AvailabilityQuery {
  token?: string;
  typeSlug?: string;
  fromISO?: string;
  toISO?: string;
  clientTz: string;
  /** Display window in days (now → now+N). The backend horizon is 60d; this is
   *  only how much the picker shows at once. Door B launches with 7. */
  pickerWindowDays?: number;
}

/** Availability outcome the page renders. Degrades HONESTLY — never invented. */
export type AvailabilityResult =
  | {
      kind: 'ok';
      type: PublicAppointmentType;
      slots: PublicSlot[];
      tz: string;
      /** R6a — pre-fill snapshot when the link came from a Contact/Project. */
      prefill: Partial<PublicClient> | null;
    }
  | {
      kind: 'error';
      reason: 'unavailable' | 'notfound' | 'appcheck' | 'appcheck_webview' | 'error';
      message: string;
    };

/** `createPublicBooking` input (datamap §3.2). */
export interface CreateBookingInput {
  token?: string;
  typeSlug?: string;
  slotStartISO: string;
  slotEndISO: string;
  clientTz: string;
  client: PublicClient;
  consent: true;
}

/** Enough about a confirmed booking for the "You're booked" + reschedule screens. */
export interface BookedInfo {
  consultationId: string;
  status: 'confirmed' | 'pending';
  startISO: string;
  endISO: string;
  meetUrl: string | null;
  /** The signed capability used for self-reschedule/cancel (datamap §2). */
  rescheduleToken: string | null;
  /**
   * Which confirmation channels the server ACTUALLY dispatched (honest-states
   * rule). Email is always-on server-side (datamap §3.5); WhatsApp self-skips
   * until the Meta templates are approved, so `whatsapp` is only ever `true`
   * when the CF confirms it went out. `undefined` = the CF didn't report → the
   * UI softens to future tense and asserts no send that didn't happen.
   */
  sentChannels?: { email?: boolean; whatsapp?: boolean };
}

/** Booking outcome. `slot_taken` carries fresh slots (spec edge flow 4.2). */
export type CreateBookingResult =
  | { kind: 'ok'; booking: BookedInfo }
  | { kind: 'slot_taken'; freshSlots: PublicSlot[] }
  | {
      kind: 'error';
      reason: 'unavailable' | 'appcheck' | 'appcheck_webview' | 'rate_limited' | 'error';
      message: string;
    };

/** `reschedulePublicBooking` outcome. */
export type RescheduleResult =
  | { kind: 'ok'; booking: BookedInfo }
  | { kind: 'slot_taken'; freshSlots: PublicSlot[] }
  | { kind: 'error'; message: string };

/** `cancelPublicBooking` outcome. */
export type CancelResult =
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

/** The seam the page depends on — identical shape for live + mock. */
export interface BookingApi {
  getAvailability(q: AvailabilityQuery): Promise<AvailabilityResult>;
  createBooking(input: CreateBookingInput): Promise<CreateBookingResult>;
  reschedule(rescheduleToken: string, newSlotStartISO: string, newSlotEndISO: string, clientTz: string): Promise<RescheduleResult>;
  cancel(rescheduleToken: string): Promise<CancelResult>;
  /** true for the mock harness — the page shows a slim "sample data" banner. */
  readonly isPreview: boolean;
}

/* ════════════════════ Seeded launch type (renders landing instantly) ════════════════════ */

/**
 * The single launch type (spec R9 / decision 8). Used to paint the landing +
 * headers with zero round-trip while live availability loads — the same
 * "render the seed immediately, refine on data" discipline as
 * `DEFAULT_AVAILABILITY`. The live CF's `type` overrides it once it arrives.
 */
export const DISCOVERY_TYPE: PublicAppointmentType = {
  name: 'Discovery / Consultation Call',
  slug: 'discovery',
  durationMin: 30,
  description:
    'A relaxed, no-obligation call to talk through your home, your plans, and how Vastu can help — measured guidance, no sales pressure.',
  host: 'Rohit Khandelwal',
  price: { mode: 'free', amount: null, currency: null },
};

/* ════════════════════ Live provider — the four App-Check-gated callables ════════════════════ */

/** Defensive coercion — the CF owns the wire shape; we read it tolerantly. */
function asString(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}
function asSlots(v: unknown): PublicSlot[] {
  if (!Array.isArray(v)) return [];
  const out: PublicSlot[] = [];
  for (const raw of v) {
    const s = asString((raw as { startISO?: unknown })?.startISO);
    const e = asString((raw as { endISO?: unknown })?.endISO);
    if (s && e) out.push({ startISO: s, endISO: e });
  }
  return out;
}

/** Pull the signed reschedule token out of the CF response (either a raw
 *  `rescheduleToken` field or a query param of `rescheduleUrl`). The backend
 *  emits the manage link as `.../book/manage?t=<token>`, so `t` MUST be read —
 *  it was the missing param that left `rescheduleToken` null and made the
 *  cancel/reschedule actions silently no-op. `rt`/`token` kept for tolerance. */
function extractRescheduleToken(raw: Record<string, unknown>): string | null {
  const direct = asString(raw.rescheduleToken);
  if (direct) return direct;
  const url = asString(raw.rescheduleUrl);
  if (url) {
    try {
      const u = new URL(url, 'https://portal.aayatanveda.com');
      return (
        u.searchParams.get('rt') ||
        u.searchParams.get('t') ||
        u.searchParams.get('token')
      );
    } catch {
      /* not a URL → no token */
    }
  }
  return null;
}

/** Read the server's dispatched-channels report defensively. Only asserts a
 *  channel when the CF explicitly reports it (accepts a few likely shapes:
 *  `sentChannels`, `sent`, or top-level `emailSent`/`whatsappSent`). Returns
 *  undefined when nothing is reported → the UI uses honest future-tense copy. */
function parseSentChannels(
  raw: Record<string, unknown>,
): { email?: boolean; whatsapp?: boolean } | undefined {
  const src = (raw.sentChannels ?? raw.sent) as Record<string, unknown> | undefined;
  const bool = (v: unknown): boolean | undefined =>
    v === true ? true : v === false ? false : undefined;
  const email = bool(src?.email) ?? bool(raw.emailSent);
  const whatsapp = bool(src?.whatsapp) ?? bool(src?.wa) ?? bool(raw.whatsappSent) ?? bool(raw.waSent);
  if (email === undefined && whatsapp === undefined) return undefined;
  return { ...(email !== undefined ? { email } : {}), ...(whatsapp !== undefined ? { whatsapp } : {}) };
}

/**
 * Sentinel thrown when the App Check token can't be obtained inside a bounded
 * window (the in-app-WebView hang). We throw BEFORE invoking the callable so the
 * Functions SDK never gets to run its own un-timed token fetch — that internal
 * fetch is exactly what hangs forever. Callers map this to `appcheck_webview`.
 */
class AppCheckWebViewError extends Error {
  constructor() {
    super('appcheck_webview');
    this.name = 'AppCheckWebViewError';
  }
}

async function callCallable<TIn extends object, TOut>(
  name: string,
  payload: TIn,
): Promise<TOut> {
  // Pre-fetch the App Check token with a HARD timeout. If the shield is
  // configured but the token can't settle (WhatsApp/Instagram WebView), DO NOT
  // call the callable — the SDK would fetch the token itself with no timeout and
  // hang forever. `unconfigured` (no site key) means App Check is off, so we
  // proceed without a token exactly as before (flip-on-ready).
  const tok = await getAppCheckTokenBounded();
  if (!tok.ok && tok.reason !== 'unconfigured') {
    throw new AppCheckWebViewError();
  }
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('../../../lib/firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TIn, TOut>(functions, name);
  return (await fn(payload)).data;
}

/** Map an unknown callable error to our honest reason. */
function errReason(e: unknown): 'appcheck' | 'appcheck_webview' | 'unavailable' {
  if (e instanceof AppCheckWebViewError) return 'appcheck_webview';
  const code = (e as { code?: string })?.code || '';
  if (/app-?check|unauthenticated|permission/i.test(code)) return 'appcheck';
  return 'unavailable';
}

export const liveBookingApi: BookingApi = {
  isPreview: false,

  async getAvailability(q) {
    try {
      const data = await callCallable<AvailabilityQuery, Record<string, unknown>>(
        'getPublicAvailability',
        q,
      );
      const rawType = (data.type ?? {}) as Record<string, unknown>;
      const type: PublicAppointmentType = {
        name: asString(rawType.name) ?? DISCOVERY_TYPE.name,
        slug: asString(rawType.slug) ?? DISCOVERY_TYPE.slug,
        durationMin:
          typeof rawType.duration === 'number'
            ? rawType.duration
            : typeof rawType.durationMin === 'number'
              ? rawType.durationMin
              : DISCOVERY_TYPE.durationMin,
        description: asString(rawType.description) ?? DISCOVERY_TYPE.description,
        host: asString(rawType.host) ?? DISCOVERY_TYPE.host,
        price:
          (rawType.price as PublicAppointmentType['price']) ?? DISCOVERY_TYPE.price,
      };
      return {
        kind: 'ok',
        type,
        slots: asSlots(data.slots),
        tz: asString(data.tz) ?? q.clientTz,
        prefill: (data.prefill as Partial<PublicClient>) ?? null,
      };
    } catch (e) {
      const reason = errReason(e);
      return {
        kind: 'error',
        reason,
        message:
          reason === 'appcheck_webview'
            ? 'To pick your time, please open this page in your phone browser (Safari or Chrome).'
            : reason === 'appcheck'
              ? "We couldn't verify your browser. Please refresh and try again."
              : "We couldn't load available times just now. Please try again in a moment.",
      };
    }
  },

  async createBooking(input) {
    try {
      const data = await callCallable<CreateBookingInput, Record<string, unknown>>(
        'createPublicBooking',
        input,
      );
      const status = data.status;
      if (status === 'slot_taken') {
        return { kind: 'slot_taken', freshSlots: asSlots(data.freshSlots ?? data.slots) };
      }
      if (data.ok === true && (status === 'confirmed' || status === 'pending')) {
        return {
          kind: 'ok',
          booking: {
            consultationId: asString(data.consultationId) ?? '',
            status: status === 'pending' ? 'pending' : 'confirmed',
            startISO: input.slotStartISO,
            endISO: input.slotEndISO,
            meetUrl: asString(data.meetUrl),
            rescheduleToken: extractRescheduleToken(data),
            sentChannels: parseSentChannels(data),
          },
        };
      }
      return {
        kind: 'error',
        reason: 'error',
        message: asString(data.reason) ?? 'That booking could not be completed. Nothing was saved — please try again.',
      };
    } catch (e) {
      const reason = errReason(e);
      return {
        kind: 'error',
        reason,
        message:
          reason === 'appcheck_webview'
            ? 'To confirm your booking, please open this page in your phone browser (Safari or Chrome). Nothing was saved.'
            : reason === 'appcheck'
              ? "We couldn't verify your browser. Please refresh and try again."
              : "We couldn't reach the booking service just now. Nothing was saved — please try again in a moment.",
      };
    }
  },

  async reschedule(rescheduleToken, newSlotStartISO, newSlotEndISO, clientTz) {
    try {
      const data = await callCallable<
        { rescheduleToken: string; newSlotStartISO: string; clientTz: string },
        Record<string, unknown>
      >('reschedulePublicBooking', { rescheduleToken, newSlotStartISO, clientTz });
      if (data.status === 'slot_taken') {
        return { kind: 'slot_taken', freshSlots: asSlots(data.freshSlots ?? data.slots) };
      }
      if (data.ok === true) {
        return {
          kind: 'ok',
          booking: {
            consultationId: asString(data.newConsultationId) ?? asString(data.consultationId) ?? '',
            status: data.status === 'pending' ? 'pending' : 'confirmed',
            startISO: newSlotStartISO,
            endISO: newSlotEndISO,
            meetUrl: asString(data.meetUrl),
            rescheduleToken: extractRescheduleToken(data) ?? rescheduleToken,
            sentChannels: parseSentChannels(data),
          },
        };
      }
      return { kind: 'error', message: asString(data.reason) ?? "We couldn't move your call. Your original time is unchanged." };
    } catch {
      return { kind: 'error', message: "We couldn't reach the booking service just now. Your original time is unchanged." };
    }
  },

  async cancel(rescheduleToken) {
    try {
      const data = await callCallable<{ rescheduleToken: string }, Record<string, unknown>>(
        'cancelPublicBooking',
        { rescheduleToken },
      );
      if (data.ok === true && data.status === 'cancelled') return { kind: 'ok' };
      return { kind: 'error', message: asString(data.reason) ?? "We couldn't cancel just now — please try again." };
    } catch {
      return { kind: 'error', message: "We couldn't reach the booking service just now — please try again." };
    }
  },
};
